/**
 * UpdateBanner — small dismissible toast pinned to the top of the
 * viewport that surfaces "a new version is ready" and gives the user
 * one-click access to ``applyUpdate()``.
 *
 * The banner is deliberately unobtrusive: it doesn't lock input,
 * doesn't cover critical UI, and stays hidden until either the
 * asset-manifest hash changes OR a waiting SW is detected.
 *
 * Rationale for a banner over auto-reload:
 *   Enterprise users may be mid-way through entering a bill or
 *   editing a journal. Forcibly reloading would lose their work.
 *   The banner lets them save first, then update on their terms.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { RefreshCw, X } from 'lucide-react';
import {
  startUpdateChecker, registerServiceWorker, applyUpdate,
} from '../lib/updateChecker';


export function UpdateBanner() {
  const [available, setAvailable] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [applying,  setApplying]  = useState(false);

  useEffect(() => {
    // 1) Register the SW so ``updatefound`` events flow.
    registerServiceWorker(() => setAvailable(true));

    // 2) Poll asset-manifest as a belt-and-suspenders detector.
    const stop = startUpdateChecker(() => setAvailable(true));

    return () => stop && stop();
  }, []);

  const handleUpdate = useCallback(async () => {
    setApplying(true);
    // ``applyUpdate`` triggers a reload — no further state work needed.
    await applyUpdate();
  }, []);

  if (!available || dismissed) return null;

  return (
    <div
      className="fixed top-4 left-1/2 -translate-x-1/2 z-[9999]
                 flex items-center gap-3 px-4 py-2.5
                 bg-indigo-600 text-white rounded-full shadow-lg
                 border border-indigo-500/40 backdrop-blur"
      role="status"
      data-testid="update-banner"
    >
      <RefreshCw size={16} className={applying ? 'animate-spin' : ''} />
      <span className="text-sm font-medium">
        يوجد إصدار جديد من النظام.
      </span>
      <button
        type="button"
        onClick={handleUpdate}
        disabled={applying}
        className="text-xs bg-white/20 hover:bg-white/30
                   rounded-full px-3 py-1 font-semibold transition"
        data-testid="update-banner-apply"
      >
        {applying ? 'جارٍ التحديث…' : 'تحديث الآن'}
      </button>
      <button
        type="button"
        onClick={() => setDismissed(true)}
        aria-label="Dismiss"
        className="text-white/70 hover:text-white transition"
        data-testid="update-banner-dismiss"
      >
        <X size={14} />
      </button>
    </div>
  );
}


export default UpdateBanner;
